import React from 'react'
import Box from '@material-ui/core/Box'
import Chip from '@material-ui/core/Chip'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import CreditCardIcon from 'components/material-symbol/icons/CreditCard'
import PlaceIcon from 'components/material-symbol/icons/Place'
import TodayIcon from 'components/material-symbol/icons/Today'
import DateRangeIcon from 'components/material-symbol/icons/DateRange'
import LinkIcon from 'components/material-symbol/icons/Link'
import LaunchIcon from 'components/material-symbol/icons/Launch'
import FileCopyIcon from 'components/material-symbol/icons/FileCopy'
import TipWell, { TipWellActionButton } from 'components/tip-well'
import { useLoadGroupEventFormValues } from 'components/group-events/create-group-event-form-wizard/hooks'
import CopyTextButton from 'components/copy-text-button'
import EventDescription from './event-description'
import EventQrCode from './event-qr-code'
import IconBadge from './icon-badge'
import { useHistory } from 'react-router-dom'
import { DateTime } from 'luxon'
import { useStyles } from './styles'
import SessionsAccordion from 'components/group-events/sessions-accordion'
import {
  formattedGroupEventPrice,
  isRecurringPayment,
} from 'utils/groupEventUtils'
import logo from 'images/pga-hope-logo.svg'
import Disclaimer from 'components/disclaimer'
import GraduateModal from '../graduate-modal'
import { getPaymentAccountWarningChip } from '../../utils'
import { composeOptimizedImageUrl } from 'lib/utils'
import { useIsMobile } from 'lib/hooks'
import { getTimePeriodWithTimezone } from 'utils/dateUtils'

const getDisplayPrice = event => {
  if (event.priceInCents === 0 && !isRecurringPayment(event)) {
    return 'Free'
  }
  return formattedGroupEventPrice(event)
}

const getStatusLabel = event => {
  if (event.isCanceled) return 'Canceled'
  if (event.isPast()) return 'Past'
  return 'Upcoming'
}

const getEventDateLines = event => {
  const session = event.sessions[0]
  if (!session) {
    return { dateLine: event.eventDayAndTime, timeLine: null }
  }

  const start = DateTime.fromISO(session.startDateTime).setZone(event.timezone)
  return {
    dateLine: start.toFormat('ccc, LLL d · yyyy'),
    timeLine: getTimePeriodWithTimezone(
      session.startDateTime,
      session.endDateTime,
      event.timezone,
    ),
  }
}

const EventDetails = ({
  event,
  actionButtons: ActionButtons,
  participants,
  stats,
  isCoachView = false,
  view = 'detail',
  statsContent = null,
  participantsContent = null,
}) => {
  const classes = useStyles()
  const history = useHistory()
  const loadGroupEventFormValues = useLoadGroupEventFormValues()
  const isMobile = useIsMobile()

  const lastSession = event?.sessions[event?.sessions.length - 1]
  const isPgaHopeEvent = event?.tags.includes('PGA HOPE')
  const showHopeGraduateTip =
    isPgaHopeEvent &&
    isCoachView &&
    new Date(lastSession?.endDateTime) < new Date()
  const paymentAccountWarningChip =
    isCoachView && !event?.isPast()
      ? getPaymentAccountWarningChip({ event, classes, isMobile })
      : null
  const { dateLine, timeLine } = getEventDateLines(event)
  const heroSubtitle = event.academy?.name || null

  return (
    <>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box className={classes.hero}>
          <Box className={classes.headerImageWrapper}>
            <img
              className={classes.headerImage}
              alt="event-header"
              src={composeOptimizedImageUrl(event.featuredImageUrl, 1200)}
            />
            <Box className={classes.headerImageOverlay} aria-hidden />
          </Box>
          {ActionButtons ? <ActionButtons /> : null}
          <Box className={classes.heroContent}>
            <Chip
              label={getStatusLabel(event)}
              size="small"
              className={classes.statusChip}
            />
            <Typography className={classes.heroTitle}>{event.title}</Typography>
            {heroSubtitle ? (
              <Typography className={classes.heroSubtitle}>
                {heroSubtitle}
              </Typography>
            ) : null}
          </Box>
          <Box className={classes.heroPrice}>
            <Typography className={classes.heroPriceLabel}>PRICE</Typography>
            <Typography className={classes.heroPriceValue}>
              {getDisplayPrice(event)}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.contentRoot}>
        {view === 'detail' ? (
          <>
            {showHopeGraduateTip ? (
              <Box className={classes.hopeBanner}>
                <Disclaimer
                  logo={logo}
                  alt="pga-hope"
                  description={
                    <>
                      <strong>{'Update Graduate Status'}</strong>
                      <Typography variant="body2" color="textSecondary">
                        {
                          'Designate or update the golfers as graduates using this link. '
                        }
                      </Typography>
                    </>
                  }
                  action={
                    <GraduateModal
                      trigger={
                        <Button variant="outlined" color="primary">
                          {'Update Status'}
                        </Button>
                      }
                      event={event}
                    />
                  }
                />
              </Box>
            ) : null}
            <Grid container spacing={0} className={classes.contentGrid}>
              <Grid item xs={12} md={5} className={classes.aboutColumn}>
                <Box className={classes.mainColumn}>
                  <Box>
                    <Typography className={classes.sectionLabel}>
                      About this event
                    </Typography>
                    <EventDescription description={event.description} />
                  </Box>
                  {event.tags.length > 0 ? (
                    <Box className={classes.tagRow}>
                      {event.tags.map(tag => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          className={classes.tagChip}
                        />
                      ))}
                    </Box>
                  ) : null}
                  {isCoachView && event.isPast() ? (
                    <Box className={classes.qrCodeBlock}>
                      <TipWell
                        stacked
                        caption={
                          <>
                            <Box
                              component="span"
                              className={classes.pastEventTipHeadline}
                            >
                              Run this event again
                            </Box>
                            This event has ended. Duplicate it to create a new
                            event with the same details.
                          </>
                        }
                        action={
                          <TipWellActionButton
                            startIcon={<FileCopyIcon fontSize="small" />}
                            onClick={() => loadGroupEventFormValues(event)}
                          >
                            Duplicate
                          </TipWellActionButton>
                        }
                      />
                    </Box>
                  ) : null}
                  {isCoachView &&
                  !event.isPast() &&
                  event.eventUrl &&
                  !event.externalRegistrationUrl ? (
                    <EventQrCode eventUrl={event.eventUrl} classes={classes} />
                  ) : null}
                </Box>
              </Grid>
              <Grid item xs={12} md={7} className={classes.detailsColumn}>
                <Box className={classes.sidebar}>
                  <Box className={classes.detailsCard}>
                    <Typography className={classes.sectionLabel}>
                      Event details
                    </Typography>
                    <Box className={classes.detailRow}>
                      <IconBadge icon={DateRangeIcon} />
                      <Box>
                        <Typography className={classes.detailPrimary}>
                          {dateLine}
                        </Typography>
                        {timeLine ? (
                          <Typography className={classes.detailSecondary}>
                            {timeLine}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                    {event.sessions.length > 1 ? (
                      <Box className={classes.sessionsAccordion}>
                        <SessionsAccordion
                          sessions={event.sessions}
                          timezone={event.timezone}
                        />
                      </Box>
                    ) : null}
                    <Box className={classes.detailRow}>
                      <IconBadge icon={PlaceIcon} />
                      <Typography className={classes.locationLink}>
                        {event.facilityName}
                      </Typography>
                    </Box>
                    <Box className={classes.detailRow}>
                      <IconBadge icon={CreditCardIcon} />
                      <Box>
                        <Typography className={classes.detailPrimary}>
                          {getDisplayPrice(event)}
                        </Typography>
                        <Typography className={classes.detailSecondary}>
                          per registration
                        </Typography>
                        {paymentAccountWarningChip ? (
                          <Box className={classes.paymentWarningRow}>
                            {paymentAccountWarningChip}
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              onClick={() =>
                                history.push(
                                  `/pga-coach/bookings/events/edit/${event.slug}#edit-section-registration`,
                                )
                              }
                            >
                              Update
                            </Button>
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                    {event.registrationOpenDateTime ? (
                      <Box className={classes.detailRow}>
                        <IconBadge icon={TodayIcon} />
                        <Box>
                          <Typography className={classes.detailPrimary}>
                            Registration opens
                          </Typography>
                          <Typography className={classes.detailSecondary}>
                            {event.registrationOpenText}
                          </Typography>
                        </Box>
                      </Box>
                    ) : null}
                    {event.registrationCloseDateTime ? (
                      <Box className={classes.detailRow}>
                        <IconBadge icon={TodayIcon} />
                        <Box>
                          <Typography className={classes.detailPrimary}>
                            Registration closes
                          </Typography>
                          <Typography className={classes.detailSecondary}>
                            {event.registrationCloseText}
                          </Typography>
                        </Box>
                      </Box>
                    ) : null}
                    <CopyTextButton
                      value={event.eventUrl}
                      className={classes.copyTextButtonStyled}
                    >
                      <Box className={classes.detailRow}>
                        <IconBadge icon={LinkIcon} />
                        <Typography className={classes.linkText}>
                          {event.eventUrl}
                        </Typography>
                      </Box>
                    </CopyTextButton>
                    {event.externalRegistrationUrl ? (
                      <CopyTextButton
                        value={event.externalRegistrationUrl}
                        className={classes.copyTextButtonStyled}
                      >
                        <Box className={classes.detailRow}>
                          <IconBadge icon={LaunchIcon} />
                          <Typography className={classes.linkText}>
                            {event.externalRegistrationUrl}
                          </Typography>
                        </Box>
                      </CopyTextButton>
                    ) : null}
                  </Box>
                  {!event.externalRegistrationUrl ? participants : null}
                  {stats}
                </Box>
              </Grid>
            </Grid>
          </>
        ) : null}
        {view === 'stats' ? statsContent : null}
        {view === 'participants' ? participantsContent : null}
      </DialogContent>
    </>
  )
}

export default EventDetails
